import React, { useState, RefObject } from "react";
import { useServices } from "../../services/Services";
import Dialog from "./Dialog";
import { DialogHandle } from "./DialogTypes";
import css from "../../common-styling/Dialog.module.css";

const REPORT_TYPE_OPTIONS = [
  {
    value: "type_1",
    label: "Geopolitical Trends and Issues",
    isDyad: false,
    commentRequired: false,
    commentHint: [
      "Input the time window (start date / end date) you would like the report to cover",
      "If nothing is defined, the report will default to the last 30 days",
      "Note any particular topics or specific events you would like covered",
    ],
  },
  {
    value: "type_2",
    label: "Regional Report",
    isDyad: false,
    commentRequired: false,
    commentHint: [
      "Input the time window (start date / end date) you would like the report to cover",
      "If nothing is defined, the report will default to the last 30 days",
      "Note any particular topics or specific events you would like covered",
    ],
  },
  {
    value: "type_3",
    label: "Flash Report",
    isDyad: false,
    commentRequired: true,
    commentHint: [
      "Input the event you want a flash report about",
      "Give as much specific information as possible about the event",
      "Include the date or date range you want for the report",
    ],
  },
  {
    value: "type_4",
    label: "Dyad Report",
    isDyad: true,
    commentRequired: false,
    commentHint: [
      "Bilateral relationship assessment across key country pairs",
      "Processing takes approximately 25 minutes",
    ],
  },
] as const;

const LOOKBACK_DAYS_DEFAULT = 30;
const LOOKBACK_DAYS_MIN = 1;
const LOOKBACK_DAYS_MAX = 365;

const inputStyle = {
  padding: "7px",
  background: "var(--main-background)",
  color: "var(--main-text)",
  border: "1px solid var(--box-border-color)",
  borderRadius: "4px",
  fontSize: "inherit",
  fontFamily: "inherit",
  width: "100%",
  boxSizing: "border-box" as const,
};

const CustomReportDialog: React.FC<{
  ref: RefObject<DialogHandle | null>;
}> = function ({ ref }) {
  const [selectedType, setSelectedType] = useState<
    (typeof REPORT_TYPE_OPTIONS)[number]
  >(REPORT_TYPE_OPTIONS[0]);
  const [sessionName, setSessionName] = useState("");
  const [comment, setComment] = useState("");
  const [lookbackDays, setLookbackDays] = useState(LOOKBACK_DAYS_DEFAULT);
  const services = useServices();
  const { data: user } = services.User.useGetCurrent();

  const createCustomReportMutation =
    services.Request.useCreateCustomReportRequest();
  const generateDyadMutation = services.Request.useGenerateAllDyadAssessments();

  async function handleSubmit() {
    if (selectedType.isDyad) {
      await generateDyadMutation.mutateAsync({
        userId: user!.id,
        lookbackDays,
      });
    } else {
      const name = sessionName.trim() || selectedType.label;
      await createCustomReportMutation.mutateAsync({
        reportType: selectedType.value,
        sessionName: name,
        userId: user!.id,
        comment: comment.trim(),
      });
    }
    setSelectedType(REPORT_TYPE_OPTIONS[0]);
    setSessionName("");
    setComment("");
    setLookbackDays(LOOKBACK_DAYS_DEFAULT);
    return false;
  }

  return (
    <Dialog
      className={css.dialog}
      ref={ref}
      title="Request Custom Report — Scheduling coming soon"
      action="Request Report"
      onSubmit={handleSubmit}
      onError={(err) => {
        const message = err instanceof Error ? err.message : String(err);
        if (message.includes("pending") || message.includes("in-progress")) {
          return "You already have a pending or in-progress dyad report request.";
        }
        return "Something went wrong requesting your custom report. If the problem persists, please contact accounts@unrulycorp.com.";
      }}
      buttonsInHeader={true}
    >
      <div className={css["field-group"]}>
        <div className={css.label}>Report Type</div>
        <div className={css["field"]}>
          <select
            value={selectedType.value}
            onChange={(e) => {
              const option = REPORT_TYPE_OPTIONS.find(
                (o) => o.value === e.target.value,
              );
              if (option) setSelectedType(option);
            }}
            style={inputStyle}
          >
            {REPORT_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {!selectedType.isDyad && (
        <div className={css["field-group"]}>
          <div className={css.label}>Session Name</div>
          <div className={css["field"]}>
            <input
              type="text"
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value.slice(0, 50))}
              maxLength={50}
              placeholder=""
              style={inputStyle}
            />
          </div>
        </div>
      )}
      {selectedType.isDyad && (
        <div className={css["field-group"]}>
          <div className={css.label}>Lookback Days</div>
          <div className={css["field"]}>
            <input
              type="number"
              value={lookbackDays}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!isNaN(v))
                  setLookbackDays(
                    Math.min(LOOKBACK_DAYS_MAX, Math.max(LOOKBACK_DAYS_MIN, v)),
                  );
              }}
              min={LOOKBACK_DAYS_MIN}
              max={LOOKBACK_DAYS_MAX}
              style={inputStyle}
            />
          </div>
        </div>
      )}
      <div className={css["field-group"]}>
        <div className={css.label}>
          Customization{selectedType.commentRequired ? "" : " (optional)"}
        </div>
        <ul className={css.hintList}>
          {selectedType.commentHint.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
        <div className={css["field"]}>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 500))}
            maxLength={500}
            required={selectedType.commentRequired}
            placeholder=""
            rows={3}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default CustomReportDialog;
